import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Modal, Table, Button, Loader, Message, Icon, Label, Placeholder, Input, Dropdown, Pagination, Dimmer } from 'semantic-ui-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import dashboardService, { ContractListItemDto } from '../../services/dashboard/dashboardService';
import { campaignService } from '../../services/campaigns/campaignService';
import { CampaignContractTableFilter, CampaignRevenueContractBucket, CampaignResponseDto } from '../../types/campaigns.types';
import StatusHistoryPopover from '../contracts/StatusHistoryPopover';
import CampaignStatusFilterDropdown from '../Campaigns/CampaignStatusFilterDropdown';
import { resolveCampaignLineColumnVisibility } from '../../utils/campaignEligibilityColumnVisibility';
import { campaignModalTableFilterRowCss, campaignModalContractsTableCss, CAMPAIGN_MODAL_CONTRACTS_TABLE_CLASS } from '../contracts/contractClientColumnStyles';
import { ContractLinesViewModal } from '../contracts/ContractLinesViewModal';

const CAMPAIGN_PAGE_SIZE_OPTIONS = [
  { key: 5, value: 5, text: '5 por página' },
  { key: 10, value: 10, text: '10 por página' },
  { key: 15, value: 15, text: '15 por página' },
  { key: 20, value: 20, text: '20 por página' },
];

const EMPTY_CAMPAIGN_FILTER: CampaignContractTableFilter = {};
const normalizeSellerName = (name?: string | null) => (name ?? '').trim().toUpperCase();
const CAMPAIGN_FILTER_DEBOUNCE_MS = 400;

interface ContractListModalProps {
  isOpen: boolean;
  onClose: () => void;
  statuses: number[] | null;
  statusLabel: string;
  mode?: 'status' | 'linhasAptas' | 'vtmeConfirmado' | 'campaign' | 'campaignPoints' | 'campaignWeeklyBonus';
  teamId?: string | null;
  periodKey?: string;
  customStartDate?: string;
  customEndDate?: string;
  getPeriodDates?: (periodKey: string, customStart?: string, customEnd?: string) => { startDate: string; endDate: string } | null;
  campaignId?: string;
  campaign?: CampaignResponseDto;
  campaignBucket?: CampaignRevenueContractBucket;
  campaignStartDate?: string;
  campaignEndDate?: string;
  weeklyBonusWindowIndex?: number;
  weeklyBonusUserId?: string;
}

const ContractListModal: React.FC<ContractListModalProps> = ({
  isOpen,
  onClose,
  statuses,
  statusLabel,
  mode = 'status',
  teamId,
  periodKey = 'esteMes',
  customStartDate = '',
  customEndDate = '',
  getPeriodDates,
  campaignId,
  campaign,
  campaignBucket,
  campaignStartDate,
  campaignEndDate,
  weeklyBonusWindowIndex,
  weeklyBonusUserId,
}) => {
  const [contracts, setContracts] = useState<ContractListItemDto[]>([]);
  const [weeklyBonusMeta, setWeeklyBonusMeta] = useState<
    Record<string, { linhasQualificadas: number; bonificacaoContrato: number }>
  >({});
  const [linesModalContract, setLinesModalContract] = useState<ContractListItemDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('ascending');
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignPageSize, setCampaignPageSize] = useState(5);
  const [campaignTotalCount, setCampaignTotalCount] = useState(0);
  const [campaignFilters, setCampaignFilters] = useState<CampaignContractTableFilter>(EMPTY_CAMPAIGN_FILTER);
  const [debouncedCampaignFilters, setDebouncedCampaignFilters] = useState<CampaignContractTableFilter>(EMPTY_CAMPAIGN_FILTER);
  const [campaignShellReady, setCampaignShellReady] = useState(false);
  const [availableStatusIds, setAvailableStatusIds] = useState<number[]>([]);
  const [availableConsultants, setAvailableConsultants] = useState<string[]>([]);
  const [availableContractTypes, setAvailableContractTypes] = useState<string[]>([]);
  const campaignTableTopRef = useRef<HTMLDivElement | null>(null);
  const campaignTableScrollRef = useRef<HTMLDivElement | null>(null);
  const { hasRole } = useAuth();
  const { showError: showExportError } = useToast();

  const isCampaignMode = mode === 'campaign';
  const isCampaignPointsMode = mode === 'campaignPoints';
  const isCampaignWeeklyBonusMode = mode === 'campaignWeeklyBonus';
  const isCampaignExportMode = isCampaignMode || isCampaignPointsMode || isCampaignWeeklyBonusMode;
  const isCampaignContractTable = isCampaignMode || isCampaignPointsMode || isCampaignWeeklyBonusMode;
  const isCampaignRevenueBucket = isCampaignMode
    && (campaignBucket === 'receitaAprovada' || campaignBucket === 'emAprovacao');
  const { showRenegColumns, showAcessosColumns } = useMemo(
    () => resolveCampaignLineColumnVisibility(campaign, {
      isCampaignRevenueBucket,
      isCampaignPointsMode,
      isCampaignWeeklyBonusMode,
      weeklyBonusWindowIndex,
    }),
    [campaign, isCampaignRevenueBucket, isCampaignPointsMode, isCampaignWeeklyBonusMode, weeklyBonusWindowIndex],
  );
  const lineMetricColumnCount = (showRenegColumns ? 2 : 0) + (showAcessosColumns ? 2 : 0);
  const campaignTotalPages = Math.max(1, Math.ceil(campaignTotalCount / campaignPageSize));
  const showBlockingLoader = loading && !(isCampaignContractTable && campaignShellReady);

  useEffect(() => {
    if (!isCampaignContractTable) return;
    const timer = setTimeout(() => setDebouncedCampaignFilters(campaignFilters), CAMPAIGN_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [campaignFilters, isCampaignContractTable]);

  const isAdminOrTeamLeader = hasRole('Admin') || hasRole('TeamLeader') || hasRole('Manager');
  const weeklyBonusColumnCount = isCampaignWeeklyBonusMode ? 2 : 0;
  const pointsColumnCount = isCampaignPointsMode ? 1 : 0;
  const campaignTableColumnCount = 6 + lineMetricColumnCount + weeklyBonusColumnCount + pointsColumnCount + 1 + 1
    + (isAdminOrTeamLeader ? 2 : 0);
  const hideConsultorFilter = isCampaignWeeklyBonusMode;
  const consultantFilterOptions = useMemo(
    () =>
      availableConsultants.map((consultant) => ({
        key: consultant,
        value: consultant,
        text: normalizeSellerName(consultant),
      })),
    [availableConsultants],
  );
  const typeFilterOptions = useMemo(
    () =>
      availableContractTypes.map((tipo) => ({
        key: tipo,
        value: tipo,
        text: tipo,
      })),
    [availableContractTypes],
  );

  // Calcular métricas de VTME para card Aprovado CQV
  const vtmeMetrics = useMemo(() => {
    if (statusLabel !== 'Aprovado CQV') return null;
    
    const total = contracts.length;
    const comVtme = contracts.filter(c => c.vtmeConfirmado === true).length;
    const semVtme = total - comVtme;
    
    return { total, comVtme, semVtme };
  }, [contracts, statusLabel]);

  // Calcular métricas de Acessos (linhas aditivas) para card Aprovado CQV
  const aditivoMetrics = useMemo(() => {
    if (statusLabel !== 'Aprovado CQV') return null;
    return {
      totalAcessos: contracts.reduce((s, c) => s + (c.linhasAditivas ?? 0), 0),
      receitaAcessos: contracts.reduce((s, c) => s + (c.receitaLinhasAditivas ?? 0), 0),
    };
  }, [contracts, statusLabel]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
    } else {
      setSortColumn(column);
      setSortDirection('ascending');
    }
  };

  const scrollToCampaignTableTop = useCallback(() => {
    const anchor = campaignTableTopRef.current;
    if (!anchor) return;

    campaignTableScrollRef.current?.scrollTo({ left: 0, behavior: 'smooth' });

    const modalScrollingContent = anchor.closest('.scrolling.content') as HTMLElement | null;
    if (modalScrollingContent) {
      modalScrollingContent.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Fallback defensivo caso a estrutura do Modal mude.
    anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleCampaignPageChange = useCallback((page: number) => {
    setCampaignPage(page);
    requestAnimationFrame(() => requestAnimationFrame(scrollToCampaignTableTop));
  }, [scrollToCampaignTableTop]);

  const sortedContracts = useMemo(() => {
    if (isCampaignContractTable) return contracts;
    if (!sortColumn) return contracts;
    const sorted = [...contracts].sort((a, b) => {
      let aVal: string | number | undefined = (a as any)[sortColumn];
      let bVal: string | number | undefined = (b as any)[sortColumn];
      if (sortColumn === 'clientName') { aVal = a.clientName; bVal = b.clientName; }
      else if (sortColumn === 'clientCnpj') { aVal = a.clientCnpj; bVal = b.clientCnpj; }
      else if (sortColumn === 'statusDisplay') { aVal = a.statusDisplay; bVal = b.statusDisplay; }
      else if (sortColumn === 'dataVenda') { aVal = a.dataVenda; bVal = b.dataVenda; }
      else if (sortColumn === 'linhasRenegociacao') { aVal = a.linhasRenegociacao ?? 0; bVal = b.linhasRenegociacao ?? 0; }
      else if (sortColumn === 'receitaLinhasRenegociacao') { aVal = a.receitaLinhasRenegociacao ?? 0; bVal = b.receitaLinhasRenegociacao ?? 0; }
      else if (sortColumn === 'linhasAditivas') { aVal = a.linhasAditivas ?? 0; bVal = b.linhasAditivas ?? 0; }
      else if (sortColumn === 'receitaLinhasAditivas') { aVal = a.receitaLinhasAditivas ?? 0; bVal = b.receitaLinhasAditivas ?? 0; }
      else if (sortColumn === 'tipoContratacao') { aVal = a.tipoContratacao; bVal = b.tipoContratacao; }
      else if (sortColumn === 'userName') { aVal = a.userName; bVal = b.userName; }
      else if (sortColumn === 'teamName') { aVal = a.teamName ?? ''; bVal = b.teamName ?? ''; }
      else if (sortColumn === 'managerName') { aVal = a.managerName ?? ''; bVal = b.managerName ?? ''; }
      else if (sortColumn === 'linhasAptas') { aVal = a.linhasAptas ?? 0; bVal = b.linhasAptas ?? 0; }
      else if (sortColumn === 'linhasNaoAptas') { aVal = a.linhasNaoAptas ?? 0; bVal = b.linhasNaoAptas ?? 0; }
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortDirection === 'ascending' ? cmp : -cmp;
    });
    return sorted;
  }, [contracts, sortColumn, sortDirection, isCampaignContractTable]);

  const loadContracts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const periodDates = getPeriodDates?.(periodKey, customStartDate, customEndDate);
      const startDate = periodDates?.startDate;
      const endDate = periodDates?.endDate;
      
      if (mode === 'linhasAptas') {
        const data = await dashboardService.getContractsByLinhasAptas(undefined, teamId, startDate, endDate);
        setContracts(data);
      } else if (mode === 'vtmeConfirmado') {
        const data = await dashboardService.getContractsByVtmeConfirmed(teamId, startDate, endDate);
        setContracts(data);
      } else if (mode === 'campaign' || mode === 'campaignPoints' || mode === 'campaignWeeklyBonus') {
        if (!campaignId || (mode === 'campaign' && !campaignBucket)) {
          setContracts([]);
          setCampaignTotalCount(0);
          setAvailableStatusIds([]);
          setWeeklyBonusMeta({});
          return;
        }
        if (mode === 'campaignWeeklyBonus') {
          if (weeklyBonusWindowIndex == null || !weeklyBonusUserId) {
            setContracts([]);
            setCampaignTotalCount(0);
            setAvailableStatusIds([]);
            setWeeklyBonusMeta({});
            return;
          }
          const data = await campaignService.getWeeklyBonusContractsPaged(
            campaignId,
            weeklyBonusWindowIndex,
            weeklyBonusUserId,
            {
              page: campaignPage,
              pageSize: campaignPageSize,
              filter: debouncedCampaignFilters,
            },
          );
          setContracts(data.items.map((item) => item.contract));
          setWeeklyBonusMeta(
            Object.fromEntries(
              data.items.map((item) => [
                item.contract.id,
                {
                  linhasQualificadas: item.linhasQualificadas,
                  bonificacaoContrato: item.bonificacaoContrato,
                },
              ]),
            ),
          );
          setCampaignTotalCount(data.totalCount);
          setAvailableStatusIds(data.availableContractStatuses ?? []);
          setAvailableConsultants(data.availableConsultants ?? []);
          setAvailableContractTypes(data.availableContractTypes ?? []);
          setCampaignShellReady(true);
        } else {
        const data = mode === 'campaignPoints'
          ? await campaignService.getPointsContractsPaged(campaignId, {
              startDate: campaignStartDate,
              endDate: campaignEndDate,
              page: campaignPage,
              pageSize: campaignPageSize,
              filter: debouncedCampaignFilters,
            })
          : await campaignService.getRevenueContractsPaged(
              campaignId,
              campaignBucket!,
              {
                startDate: campaignStartDate,
                endDate: campaignEndDate,
                page: campaignPage,
                pageSize: campaignPageSize,
                filter: debouncedCampaignFilters,
              }
            );
        setContracts(data.items);
        setCampaignTotalCount(data.totalCount);
        setAvailableStatusIds(data.availableContractStatuses ?? []);
        setAvailableConsultants(data.availableConsultants ?? []);
        setAvailableContractTypes(data.availableContractTypes ?? []);
        setCampaignShellReady(true);
        setWeeklyBonusMeta({});
        }
      } else {
        const data = await dashboardService.getContractsByStatus(
          statuses !== null ? statuses : undefined,
          undefined,
          teamId,
          startDate,
          endDate
        );
        setContracts(data);
      }
    } catch (err: any) {
      console.error('Erro ao carregar contratos:', err);
      setError(err.message || 'Erro ao carregar contratos');
    } finally {
      setLoading(false);
    }
  }, [statuses, teamId, mode, periodKey, customStartDate, customEndDate, getPeriodDates, campaignId, campaignBucket, campaignStartDate, campaignEndDate, campaignPage, campaignPageSize, debouncedCampaignFilters, weeklyBonusWindowIndex, weeklyBonusUserId]);

  useEffect(() => {
    if (isOpen) {
      loadContracts();
    } else {
      setContracts([]);
      setError(null);
      setCampaignFilters(EMPTY_CAMPAIGN_FILTER);
      setDebouncedCampaignFilters(EMPTY_CAMPAIGN_FILTER);
      setCampaignShellReady(false);
      setAvailableStatusIds([]);
      setAvailableConsultants([]);
      setAvailableContractTypes([]);
      setCampaignPage(1);
      setWeeklyBonusMeta({});
      setLinesModalContract(null);
    }
  }, [isOpen, loadContracts]);

  const handleExport = async () => {
    if (mode === 'linhasAptas') return;
    try {
      setExporting(true);
      if (mode === 'campaign') {
        if (!campaignId || !campaignBucket) return;
        await campaignService.exportRevenueContracts(campaignId, campaignBucket, 'xlsx', {
          startDate: campaignStartDate,
          endDate: campaignEndDate,
          filter: campaignFilters,
        });
        return;
      }
      if (mode === 'campaignPoints') {
        if (!campaignId) return;
        await campaignService.exportPointsContracts(campaignId, 'xlsx', {
          startDate: campaignStartDate,
          endDate: campaignEndDate,
        });
        return;
      }
      if (mode === 'campaignWeeklyBonus') {
        if (!campaignId || weeklyBonusWindowIndex == null || !weeklyBonusUserId) return;
        await campaignService.exportWeeklyBonusContracts(
          campaignId,
          weeklyBonusWindowIndex,
          weeklyBonusUserId,
          'xlsx',
          { filter: debouncedCampaignFilters },
        );
        return;
      }
      const periodDates = getPeriodDates?.(periodKey, customStartDate, customEndDate);
      const startDate = periodDates?.startDate;
      const endDate = periodDates?.endDate;
      if (mode === 'vtmeConfirmado') {
        await dashboardService.exportContractsByVtmeConfirmed(teamId, startDate, endDate);
      } else {
        await dashboardService.exportContractsByStatus(
          statuses !== null ? statuses : undefined,
          undefined,
          teamId,
          startDate,
          endDate
        );
      }
    } catch (err: any) {
      console.error('Erro ao exportar contratos:', err);
      showExportError(err?.message || 'Erro ao exportar contratos');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!isCampaignExportMode || !campaignId) return;
    try {
      setExporting(true);
      if (mode === 'campaignPoints') {
        await campaignService.exportPointsContracts(campaignId, 'pdf', {
          startDate: campaignStartDate,
          endDate: campaignEndDate,
        });
        return;
      }
      if (mode === 'campaignWeeklyBonus') {
        if (weeklyBonusWindowIndex == null || !weeklyBonusUserId) return;
        await campaignService.exportWeeklyBonusContracts(
          campaignId,
          weeklyBonusWindowIndex,
          weeklyBonusUserId,
          'pdf',
          { filter: debouncedCampaignFilters },
        );
        return;
      }
      if (!campaignBucket) return;
      await campaignService.exportRevenueContracts(campaignId, campaignBucket, 'pdf', {
        startDate: campaignStartDate,
        endDate: campaignEndDate,
        filter: campaignFilters,
      });
    } catch (err: any) {
      console.error('Erro ao exportar contratos:', err);
      showExportError(err?.message || 'Erro ao exportar PDF');
    } finally {
      setExporting(false);
    }
  };

  const updateCampaignFilter = (patch: Partial<CampaignContractTableFilter>) => {
    setCampaignPage(1);
    setCampaignFilters((prev) => {
      const next = { ...prev, ...patch };
      (Object.keys(patch) as (keyof CampaignContractTableFilter)[]).forEach((key) => {
        if (patch[key] === undefined) delete next[key];
      });
      return next;
    });
  };

  const formatCurrency = (value: number) => {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const calculateTimeInStatus = (updatedAt: string): string => {
    const updated = new Date(updatedAt);
    const now = new Date();
    const diffMs = now.getTime() - updated.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return `${diffDays}d ${diffHours}h`;
    } else if (diffHours > 0) {
      return `${diffHours}h`;
    } else {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes}min`;
    }
  };

  const getStatusColor = (status: number | null): 'blue' | 'orange' | 'yellow' | 'green' | 'purple' | 'red' | 'grey' | 'teal' => {
    if (!status) return 'grey';
    
    switch(status) {
      case 0: return 'blue';      // Criado
      case 1: return 'orange';    // Assinado
      case 2: return 'yellow';    // EmAnalise
      case 3: return 'green';     // Ativo
      case 35: return 'purple';   // AprovadoCQV
      case 36: return 'red';      // RecusadoCQV
      case 40: return 'red';      // RecusadoQualidade
      case 41: return 'red';      // RecusadoCVP
      case 42: return 'yellow';   // TrocaDeCarteira
      case 62: return 'blue';     // TrocaDeCarteiraSolicitada
      case 61: return 'red';      // TrocaDeCarteiraNaoAutorizada
      case 5: return 'grey';      // Cancelado
      default: return 'teal';
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="fullscreen"
      closeOnDimmerClick={false}
    >
      <Modal.Header>
        <Icon name="file alternate outline" />
        Contratos - {statusLabel}
        {(isCampaignContractTable ? campaignTotalCount : contracts.length) > 0 && (
          <Label color="blue" style={{ marginLeft: '10px' }}>
            {isCampaignContractTable ? campaignTotalCount : contracts.length}{' '}
            {isCampaignContractTable
              ? (campaignTotalCount === 1 ? 'contrato' : 'contratos')
              : (contracts.length === 1 ? 'contrato' : 'contratos')}
          </Label>
        )}
      </Modal.Header>

      {/* Métricas de VTME e Acessos para card Aprovado CQV */}
      {vtmeMetrics && !loading && contracts.length > 0 && (
        <Modal.Content style={{ padding: '15px 20px', background: '#f9f9f9', borderBottom: '1px solid #ddd' }}>
          <div style={{ display: 'flex', gap: '30px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center', padding: '10px' }}>
              <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#2ecc71' }}>
                {vtmeMetrics.comVtme}
              </div>
              <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                <Icon name="check circle" color="green" />
                VTME Confirmado
              </div>
            </div>
            <div style={{ height: '50px', width: '1px', background: '#ddd' }} />
            <div style={{ textAlign: 'center', padding: '10px' }}>
              <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#e67e22' }}>
                {vtmeMetrics.semVtme}
              </div>
              <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                <Icon name="clock outline" color="orange" />
                Aguardando VTME
              </div>
            </div>
            <div style={{ height: '50px', width: '1px', background: '#ddd' }} />
            <div style={{ textAlign: 'center', padding: '10px' }}>
              <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#3498db' }}>
                {vtmeMetrics.total}
              </div>
              <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                <Icon name="list" color="blue" />
                Contratos
              </div>
            </div>
            {aditivoMetrics && (
              <>
                <div style={{ height: '50px', width: '2px', background: '#bbb', margin: '0 5px' }} />
                <div style={{ textAlign: 'center', padding: '10px' }}>
                  <div style={{ fontSize: '2em', fontWeight: 'bold', color: '#8e44ad' }}>
                    {aditivoMetrics.totalAcessos}
                  </div>
                  <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                    <Icon name="plus circle" color="purple" />
                    Total Acessos
                  </div>
                </div>
                <div style={{ height: '50px', width: '1px', background: '#ddd' }} />
                <div style={{ textAlign: 'center', padding: '10px', wordBreak: 'break-word', maxWidth: '140px' }}>
                  <div style={{ fontSize: '1.3em', fontWeight: 'bold', color: '#8e44ad' }}>
                    {formatCurrency(aditivoMetrics.receitaAcessos)}
                  </div>
                  <div style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
                    <Icon name="dollar sign" color="purple" />
                    Receita Acessos
                  </div>
                </div>
              </>
            )}
          </div>
        </Modal.Content>
      )}

      <Modal.Content scrolling style={{ maxHeight: 'calc(100vh - 250px)', position: 'relative' }}>
        <div ref={campaignTableTopRef} />
        {showBlockingLoader ? (
          <div style={{ padding: '20px' }}>
            <Loader active inline="centered" size="large">Carregando contratos...</Loader>
            <Placeholder fluid style={{ marginTop: '20px' }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Placeholder.Paragraph key={i}>
                  <Placeholder.Line length="full" />
                  <Placeholder.Line length="medium" />
                </Placeholder.Paragraph>
              ))}
            </Placeholder>
          </div>
        ) : error && !(isCampaignContractTable && campaignShellReady) ? (
          <Message negative>
            <Message.Header>Erro</Message.Header>
            <p>{error}</p>
          </Message>
        ) : !isCampaignContractTable && contracts.length === 0 ? (
          <Message info>
            <Message.Header>Nenhum contrato encontrado</Message.Header>
            <p>Não há contratos com o status "{statusLabel}" para os filtros selecionados.</p>
          </Message>
        ) : (
          <>
          {error && isCampaignContractTable && campaignShellReady && (
            <Message negative style={{ marginBottom: 12 }}>
              <Message.Header>Erro ao atualizar</Message.Header>
              <p>{error}</p>
            </Message>
          )}
          {isCampaignContractTable && (
            <Dimmer active={loading} inverted>
              <Loader>Carregando contratos...</Loader>
            </Dimmer>
          )}
          {isCampaignContractTable && <style>{campaignModalTableFilterRowCss}{campaignModalContractsTableCss}</style>}
          {isCampaignContractTable && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <Dropdown
                selection
                compact
                options={CAMPAIGN_PAGE_SIZE_OPTIONS}
                value={campaignPageSize}
                onChange={(_, data) => {
                  setCampaignPage(1);
                  setCampaignPageSize(Number(data.value));
                }}
                style={{ minWidth: 140 }}
              />
              <button
                type="button"
                onClick={() => setCampaignFilters(EMPTY_CAMPAIGN_FILTER)}
                style={{
                  border: '1px solid rgba(0,0,0,0.15)',
                  borderRadius: 6,
                  background: 'rgba(255,255,255,0.6)',
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap',
                }}
              >
                Limpar filtros
              </button>
            </div>
          )}
          <div
            ref={isCampaignContractTable ? campaignTableScrollRef : undefined}
            className={isCampaignContractTable ? 'campaign-modal-contracts-scroll' : undefined}
          >
          <Table
            celled
            striped
            selectable
            sortable={!isCampaignContractTable}
            className={isCampaignContractTable ? `${CAMPAIGN_MODAL_CONTRACTS_TABLE_CLASS}${isAdminOrTeamLeader ? '' : ' without-team-cols'}` : undefined}
          >
            {isCampaignContractTable && (
              <colgroup>
                <col className="col-client" />
                <col className="col-cnpj" />
                <col className="col-status" />
                <col className="col-data-venda" />
                {showRenegColumns && (
                  <>
                    <col className="col-reneg" />
                    <col className="col-reneg-valor" />
                  </>
                )}
                {showAcessosColumns && (
                  <>
                    <col className="col-acessos" />
                    <col className="col-acessos-valor" />
                  </>
                )}
                {isCampaignPointsMode && <col className="col-pontos" />}
                {isCampaignWeeklyBonusMode && (
                  <>
                    <col className="col-bonif-linhas" />
                    <col className="col-bonif-contrato" />
                  </>
                )}
                <col className="col-tipo" />
                <col className="col-consultor" />
                {isAdminOrTeamLeader && (
                  <>
                    <col className="col-time" />
                    <col className="col-gerente" />
                  </>
                )}
              </colgroup>
            )}
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell sorted={sortColumn === 'clientName' ? sortDirection : undefined} onClick={() => handleSort('clientName')} style={{ cursor: 'pointer' }} className={isCampaignContractTable ? 'col-client' : undefined}>Cliente</Table.HeaderCell>
                <Table.HeaderCell sorted={sortColumn === 'clientCnpj' ? sortDirection : undefined} onClick={() => handleSort('clientCnpj')} style={{ cursor: 'pointer' }} className={isCampaignContractTable ? 'col-cnpj' : undefined}>CNPJ</Table.HeaderCell>
                <Table.HeaderCell sorted={sortColumn === 'statusDisplay' ? sortDirection : undefined} onClick={() => handleSort('statusDisplay')} style={{ cursor: 'pointer' }} className={isCampaignContractTable ? 'col-status' : undefined}>Status Atual</Table.HeaderCell>
                {!isCampaignContractTable && (
                  <Table.HeaderCell>Tempo no Status</Table.HeaderCell>
                )}
                <Table.HeaderCell sorted={sortColumn === 'dataVenda' ? sortDirection : undefined} onClick={() => handleSort('dataVenda')} style={{ cursor: 'pointer' }} className={isCampaignContractTable ? 'col-data-venda' : undefined}>Data Venda</Table.HeaderCell>
                {(!isCampaignContractTable || showRenegColumns) && (
                  <>
                    <Table.HeaderCell sorted={sortColumn === 'linhasRenegociacao' ? sortDirection : undefined} onClick={() => handleSort('linhasRenegociacao')} style={{ cursor: 'pointer' }} className={isCampaignContractTable ? 'col-reneg' : undefined}>RENEG</Table.HeaderCell>
                    <Table.HeaderCell sorted={sortColumn === 'receitaLinhasRenegociacao' ? sortDirection : undefined} onClick={() => handleSort('receitaLinhasRenegociacao')} style={{ cursor: 'pointer' }} className={isCampaignContractTable ? 'col-reneg-valor' : undefined}>R$ RENEG</Table.HeaderCell>
                  </>
                )}
                {(!isCampaignContractTable || showAcessosColumns) && (
                  <>
                    <Table.HeaderCell sorted={sortColumn === 'linhasAditivas' ? sortDirection : undefined} onClick={() => handleSort('linhasAditivas')} style={{ cursor: 'pointer' }} className={isCampaignContractTable ? 'col-acessos' : undefined}>Acessos</Table.HeaderCell>
                    <Table.HeaderCell sorted={sortColumn === 'receitaLinhasAditivas' ? sortDirection : undefined} onClick={() => handleSort('receitaLinhasAditivas')} style={{ cursor: 'pointer' }} className={isCampaignContractTable ? 'col-acessos-valor' : undefined}>R$ Acessos</Table.HeaderCell>
                  </>
                )}
                {isCampaignPointsMode && (
                  <Table.HeaderCell className="col-pontos">Pontos</Table.HeaderCell>
                )}
                {isCampaignWeeklyBonusMode && (
                  <>
                    <Table.HeaderCell className="col-bonif-linhas">Linhas qualificadas</Table.HeaderCell>
                    <Table.HeaderCell className="col-bonif-contrato">Bonif. contrato</Table.HeaderCell>
                  </>
                )}
                {mode === 'linhasAptas' && (
                  <>
                    <Table.HeaderCell sorted={sortColumn === 'linhasAptas' ? sortDirection : undefined} onClick={() => handleSort('linhasAptas')} style={{ cursor: 'pointer' }}>Linhas Aptas</Table.HeaderCell>
                    <Table.HeaderCell sorted={sortColumn === 'linhasNaoAptas' ? sortDirection : undefined} onClick={() => handleSort('linhasNaoAptas')} style={{ cursor: 'pointer' }}>Linhas Não Aptas</Table.HeaderCell>
                  </>
                )}
                <Table.HeaderCell sorted={sortColumn === 'tipoContratacao' ? sortDirection : undefined} onClick={() => handleSort('tipoContratacao')} style={{ cursor: 'pointer' }} className={isCampaignContractTable ? 'col-tipo' : undefined}>Tipo</Table.HeaderCell>
                <Table.HeaderCell sorted={sortColumn === 'userName' ? sortDirection : undefined} onClick={() => handleSort('userName')} style={{ cursor: 'pointer' }} className={isCampaignContractTable ? 'col-consultor' : undefined}>Consultor</Table.HeaderCell>
                {isAdminOrTeamLeader && (
                  <>
                    <Table.HeaderCell sorted={sortColumn === 'teamName' ? sortDirection : undefined} onClick={() => handleSort('teamName')} style={{ cursor: 'pointer' }} className={isCampaignContractTable ? 'col-time' : undefined}>Time</Table.HeaderCell>
                    <Table.HeaderCell sorted={sortColumn === 'managerName' ? sortDirection : undefined} onClick={() => handleSort('managerName')} style={{ cursor: 'pointer' }} className={isCampaignContractTable ? 'col-gerente' : undefined}>Gerente</Table.HeaderCell>
                  </>
                )}
              </Table.Row>
              {isCampaignContractTable && (
                <Table.Row className="campaign-table-filter-row">
                  <Table.HeaderCell className="col-client">
                    <Input
                      icon="search"
                      iconPosition="left"
                      placeholder="Cliente"
                      fluid
                      size="small"
                      value={campaignFilters.cliente ?? ''}
                      onChange={(e) => updateCampaignFilter({ cliente: e.target.value || undefined })}
                    />
                  </Table.HeaderCell>
                  <Table.HeaderCell className="col-cnpj">
                    <Input
                      placeholder="CNPJ"
                      fluid
                      size="small"
                      value={campaignFilters.cnpj ?? ''}
                      onChange={(e) => updateCampaignFilter({ cnpj: e.target.value || undefined })}
                    />
                  </Table.HeaderCell>
                  <Table.HeaderCell className="col-status">
                    <CampaignStatusFilterDropdown
                      fluid
                      availableStatusIds={availableStatusIds}
                      value={campaignFilters.status}
                      onChange={(status) => updateCampaignFilter({ status })}
                    />
                  </Table.HeaderCell>
                  <Table.HeaderCell className="col-data-venda">
                    <div className="campaign-table-filter-date-stack">
                      <Input
                        type="date"
                        fluid
                        size="small"
                        title="Data venda de"
                        value={campaignFilters.dataFrom ?? ''}
                        onChange={(e) => updateCampaignFilter({ dataFrom: e.target.value || undefined })}
                      />
                      <Input
                        type="date"
                        fluid
                        size="small"
                        title="Data venda até"
                        value={campaignFilters.dataTo ?? ''}
                        onChange={(e) => updateCampaignFilter({ dataTo: e.target.value || undefined })}
                      />
                    </div>
                  </Table.HeaderCell>
                  {showRenegColumns && (
                    <>
                      <Table.HeaderCell className="col-reneg" />
                      <Table.HeaderCell className="col-reneg-valor" />
                    </>
                  )}
                  {showAcessosColumns && (
                    <>
                      <Table.HeaderCell className="col-acessos" />
                      <Table.HeaderCell className="col-acessos-valor" />
                    </>
                  )}
                  {isCampaignPointsMode && <Table.HeaderCell className="col-pontos" />}
                  {isCampaignWeeklyBonusMode && (
                    <>
                      <Table.HeaderCell className="col-bonif-linhas" />
                      <Table.HeaderCell className="col-bonif-contrato" />
                    </>
                  )}
                  <Table.HeaderCell className="col-tipo">
                    <Dropdown
                      placeholder="Tipo"
                      fluid
                      selection
                      search
                      clearable
                      selectOnBlur={false}
                      options={typeFilterOptions}
                      value={campaignFilters.tipo ?? undefined}
                      onChange={(_, data) => updateCampaignFilter({ tipo: (data.value as string) || undefined })}
                    />
                  </Table.HeaderCell>
                  <Table.HeaderCell className="col-consultor">
                    {hideConsultorFilter ? (
                      <span style={{ color: '#666', fontSize: '0.85em' }}>Consultor</span>
                    ) : (
                    <Dropdown
                      placeholder="Consultor"
                      fluid
                      selection
                      search
                      clearable
                      selectOnBlur={false}
                      options={consultantFilterOptions}
                      value={campaignFilters.consultor ?? undefined}
                      onChange={(_, data) => updateCampaignFilter({ consultor: (data.value as string) || undefined })}
                    />
                    )}
                  </Table.HeaderCell>
                  {isAdminOrTeamLeader && (
                    <>
                      <Table.HeaderCell className="col-time">
                        <Input
                          placeholder="Time"
                          fluid
                          size="small"
                          value={campaignFilters.time ?? ''}
                          onChange={(e) => updateCampaignFilter({ time: e.target.value || undefined })}
                        />
                      </Table.HeaderCell>
                      <Table.HeaderCell className="col-gerente">
                        <Input
                          placeholder="Gerente"
                          fluid
                          size="small"
                          value={campaignFilters.gerente ?? ''}
                          onChange={(e) => updateCampaignFilter({ gerente: e.target.value || undefined })}
                        />
                      </Table.HeaderCell>
                    </>
                  )}
                </Table.Row>
              )}
            </Table.Header>
            <Table.Body>
              {sortedContracts.length === 0 && isCampaignContractTable ? (
                <Table.Row>
                  <Table.Cell colSpan={campaignTableColumnCount} textAlign="center">
                    Nenhum contrato encontrado para os filtros selecionados.
                  </Table.Cell>
                </Table.Row>
              ) : (
              sortedContracts.map((contract) => (
                <Table.Row key={contract.id}>
                  <Table.Cell className={isCampaignContractTable ? 'col-client' : undefined}>
                    <strong>{contract.clientName}</strong>
                    {contract.isGrouped && (
                      <Label size="mini" color="purple" style={{ marginLeft: '8px' }}>
                        Agrupado ({contract.groupedCount})
                      </Label>
                    )}
                  </Table.Cell>
                  <Table.Cell className={isCampaignContractTable ? 'col-cnpj' : undefined}>{contract.clientCnpj}</Table.Cell>
                  <Table.Cell className={isCampaignContractTable ? 'col-status' : undefined}>
                    <div className={isCampaignContractTable ? 'campaign-status-cell' : undefined} style={isCampaignContractTable ? undefined : { display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Label color={getStatusColor(contract.status)} size="small">
                        {contract.statusDisplay}
                      </Label>
                      <StatusHistoryPopover
                        contractId={contract.id}
                        currentStatus={contract.status}
                        currentStatusText={contract.statusDisplay}
                        updatedAt={contract.updatedAt}
                      />
                    </div>
                  </Table.Cell>
                  {!isCampaignContractTable && (
                    <Table.Cell>
                      {calculateTimeInStatus(contract.updatedAt)}
                    </Table.Cell>
                  )}
                  <Table.Cell className={isCampaignContractTable ? 'col-data-venda' : undefined}>{formatDate(contract.dataVenda)}</Table.Cell>
                  {(!isCampaignContractTable || showRenegColumns) && (
                    <>
                      <Table.Cell style={{ textAlign: 'center' }} className={isCampaignContractTable ? 'col-reneg' : undefined}>
                        <Label circular color="teal">
                          {contract.linhasRenegociacao ?? 0}
                        </Label>
                      </Table.Cell>
                      <Table.Cell className={isCampaignContractTable ? 'col-reneg-valor' : undefined}>
                        <strong>{formatCurrency(contract.receitaLinhasRenegociacao ?? 0)}</strong>
                      </Table.Cell>
                    </>
                  )}
                  {(!isCampaignContractTable || showAcessosColumns) && (
                    <>
                      <Table.Cell style={{ textAlign: 'center' }} className={isCampaignContractTable ? 'col-acessos' : undefined}>
                        <Label circular color="purple">
                          {contract.linhasAditivas ?? 0}
                        </Label>
                      </Table.Cell>
                      <Table.Cell className={isCampaignContractTable ? 'col-acessos-valor' : undefined}>
                        <strong>{formatCurrency(contract.receitaLinhasAditivas ?? 0)}</strong>
                      </Table.Cell>
                    </>
                  )}
                  {isCampaignPointsMode && (
                    <Table.Cell className="col-pontos">
                      <Label color="orange" size="small">
                        <Icon name="star" />
                        {(contract.pontos ?? 0).toFixed(1)}
                      </Label>
                    </Table.Cell>
                  )}
                  {isCampaignWeeklyBonusMode && (
                    <>
                      <Table.Cell className="col-bonif-linhas">
                        <Label
                          color="blue"
                          size="small"
                          style={{ cursor: 'pointer' }}
                          onClick={() => setLinesModalContract(contract)}
                          title="Clique para ver detalhes das linhas"
                        >
                          <Icon name="list" />
                          {weeklyBonusMeta[contract.id]?.linhasQualificadas ?? 0}{' '}
                          {(weeklyBonusMeta[contract.id]?.linhasQualificadas ?? 0) === 1 ? 'linha' : 'linhas'}
                        </Label>
                      </Table.Cell>
                      <Table.Cell className="col-bonif-contrato">
                        <Label color="teal" size="small">
                          <Icon name="money" />
                          {formatCurrency(weeklyBonusMeta[contract.id]?.bonificacaoContrato ?? 0)}
                        </Label>
                      </Table.Cell>
                    </>
                  )}
                  {mode === 'linhasAptas' && (
                    <>
                      <Table.Cell style={{ textAlign: 'center' }}>
                        <Label circular color="green">{contract.linhasAptas ?? 0}</Label>
                      </Table.Cell>
                      <Table.Cell style={{ textAlign: 'center' }}>
                        <Label circular color="orange">{contract.linhasNaoAptas ?? 0}</Label>
                      </Table.Cell>
                    </>
                  )}
                  <Table.Cell className={isCampaignContractTable ? 'col-tipo' : undefined}>{contract.tipoContratacao}</Table.Cell>
                  <Table.Cell className={isCampaignContractTable ? 'col-consultor' : undefined}>
                    {isCampaignContractTable ? (
                      <div className="campaign-consultor-cell">
                        <span className="campaign-consultor-name">{normalizeSellerName(contract.userName)}</span>
                        <span className="campaign-consultor-email">{contract.userEmail}</span>
                      </div>
                    ) : (
                      <>
                        {normalizeSellerName(contract.userName)}
                        <div style={{ fontSize: '0.85em', color: '#666' }}>
                          {contract.userEmail}
                        </div>
                      </>
                    )}
                  </Table.Cell>
                  {isAdminOrTeamLeader && (
                    <>
                      <Table.Cell className={isCampaignContractTable ? 'col-time' : undefined}>{contract.teamName || '-'}</Table.Cell>
                      <Table.Cell className={isCampaignContractTable ? 'col-gerente' : undefined}>{contract.managerName || '-'}</Table.Cell>
                    </>
                  )}
                </Table.Row>
              ))
              )}
            </Table.Body>
          </Table>
          </div>
          {isCampaignContractTable && campaignTotalCount > campaignPageSize && (
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                activePage={campaignPage}
                totalPages={campaignTotalPages}
                onPageChange={(_, data) => handleCampaignPageChange(Number(data.activePage ?? 1))}
                siblingRange={1}
                boundaryRange={1}
                ellipsisItem={null}
                firstItem={null}
                lastItem={null}
              />
            </div>
          )}
          </>
        )}
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="close" />
          Fechar
        </Button>
        {((isCampaignExportMode && campaignTotalCount > 0) || (contracts.length > 0 && mode !== 'linhasAptas' && !isCampaignContractTable)) && (
          <Button
            positive
            onClick={handleExport}
            loading={exporting}
            disabled={exporting}
          >
            <Icon name="file excel" />
            Exportar Excel
          </Button>
        )}
        {isCampaignExportMode && campaignTotalCount > 0 && (
          <Button
            color="blue"
            onClick={handleExportPdf}
            loading={exporting}
            disabled={exporting}
          >
            <Icon name="file pdf" />
            Exportar PDF
          </Button>
        )}
      </Modal.Actions>
      <ContractLinesViewModal
        open={Boolean(linesModalContract)}
        onClose={() => setLinesModalContract(null)}
        contractId={linesModalContract?.id ?? ''}
        contractInfo={
          linesModalContract
            ? {
                clientRazaoSocial: linesModalContract.clientName,
                clientCnpj: linesModalContract.clientCnpj,
              }
            : undefined
        }
        campaignContext={
          isCampaignWeeklyBonusMode && campaignId && weeklyBonusWindowIndex != null && weeklyBonusUserId
            ? {
                campaignId,
                weeklyBonus: {
                  windowIndex: weeklyBonusWindowIndex,
                  userId: weeklyBonusUserId,
                },
              }
            : undefined
        }
      />
    </Modal>
  );
};

export default ContractListModal;
